import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Plus, Edit2, Trash2, X, ChefHat, Save } from "lucide-react";
import CatalogoSubnav from "../../components/CatalogoSubnav";
import { ConfiguracionOperativaFields } from "../../components/ConfiguracionOperativaFields";

const MenusCRUD = () => {
  const [menus, setMenus] = useState([]);
  const [recetas, setRecetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null); // null = no modal, {} = nuevo, {menu_id...} = edit
  const [saving, setSaving] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const [mRes, rRes] = await Promise.all([
        axios.get(`${API}/menus`),
        axios.get(`${API}/recetas`).catch(() => ({ data: [] })),
      ]);
      setMenus((mRes.data || []).filter(m => m.nombre?.trim()));
      setRecetas(rRes.data || []);
    } catch (e) {
      toast.error("Error al cargar menús");
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const cargarPlantillas = async () => {
    setSeedLoading(true);
    try {
      const { data } = await axios.post(`${API}/tenant/seed-catalogo-demo`);
      const n = data?.agregados?.menus || 0;
      if (n > 0) toast.success(`${n} menús agregados. Puedes editarlos cuando quieras.`);
      else toast.info("Ya tenías los menús de ejemplo cargados.");
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar plantillas");
    } finally { setSeedLoading(false); }
  };

  const abrirNuevo = () => setEditando({ nombre: "", descripcion: "", precio_por_persona: 0, platillos: [], botanas: "", plato_fuerte: "", postres: "", bebidas: [] });
  const abrirEditar = (m) => setEditando({ ...m, platillos: m.platillos || [] });
  const cerrar = () => setEditando(null);

  const guardar = async () => {
    if (!editando.nombre?.trim()) { toast.error("El menú necesita un nombre"); return; }
    setSaving(true);
    try {
      if (editando.menu_id) {
        await axios.put(`${API}/menus/${editando.menu_id}`, editando);
        toast.success("Menú actualizado");
      } else {
        await axios.post(`${API}/menus`, editando);
        toast.success("Menú creado");
      }
      cerrar();
      cargar();
    } catch (e) {
      toast.error("Error al guardar: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const eliminar = async (m) => {
    if (!window.confirm(`¿Eliminar el menú "${m.nombre}"?`)) return;
    try {
      await axios.delete(`${API}/menus/${m.menu_id}`);
      toast.success("Eliminado");
      cargar();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const togglePlatillo = (receta) => {
    const ya = editando.platillos.find(p => p.receta_id === receta.receta_id);
    if (ya) {
      setEditando({ ...editando, platillos: editando.platillos.filter(p => p.receta_id !== receta.receta_id) });
    } else {
      setEditando({ ...editando, platillos: [...editando.platillos, { receta_id: receta.receta_id, nombre: receta.nombre, porciones: receta.porciones_base || 50 }] });
    }
  };

  if (loading) return <div className="p-8 text-sm text-stone-500">Cargando menús…</div>;

  return (
    <div className="space-y-6" data-testid="page-menus">
      <CatalogoSubnav />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-stone-900 flex items-center gap-2">
            <ChefHat className="w-7 h-7 text-emerald-700" /> Menús
          </h1>
          <p className="text-sm text-stone-600 mt-1">Combina recetas en menús reutilizables que puedes elegir desde el expediente del evento.</p>
        </div>
        <button onClick={abrirNuevo} className="eo-btn eo-btn-primary" data-testid="btn-nuevo-menu">
          <Plus className="w-4 h-4" /> Nuevo menú
        </button>
      </div>

      {menus.length === 0 ? (
        <div className="eo-card p-10 text-center" data-testid="empty-menus">
          <ChefHat className="w-12 h-12 mx-auto text-stone-300 mb-3" />
          <p className="font-display text-xl text-stone-900 mb-1">Aún no hay menús</p>
          <p className="text-sm text-stone-500 mb-4">Crea tu primer menú combinando recetas de tu catálogo, o carga menús de ejemplo y edítalos.</p>
          <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
            <button onClick={abrirNuevo} className="eo-btn eo-btn-primary" data-testid="btn-crear-primer-menu"><Plus className="w-4 h-4" /> Crear primer menú</button>
            <button onClick={cargarPlantillas} disabled={seedLoading} className="eo-btn eo-btn-secondary" data-testid="btn-cargar-plantillas-menus">
              {seedLoading ? "Cargando…" : "Cargar plantillas de ejemplo"}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {menus.map(m => (
            <div key={m.menu_id} className="eo-card p-4" data-testid={`menu-card-${m.menu_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="font-display text-lg text-stone-900 truncate">{m.nombre || "Sin nombre"}</p>
                  {m.categoria && (
                    <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-medium" data-testid={`chip-categoria-${m.menu_id}`}>
                      {({desayuno:"Desayuno",almuerzo:"Almuerzo",brunch:"Brunch",comida:"Comida",cena:"Cena",coffee_break:"Coffee break",snacks:"Snacks / carrito",botanas:"Botanas",postres:"Postres",buffet:"Buffet"})[m.categoria] || m.categoria}
                    </span>
                  )}
                  {m.descripcion && <p className="text-xs text-stone-500 truncate mt-0.5">{m.descripcion}</p>}
                </div>
                <div className="flex gap-0.5 shrink-0">
                  <button onClick={() => abrirEditar(m)} className="p-1.5 rounded hover:bg-stone-100" title="Editar" data-testid={`btn-editar-menu-${m.menu_id}`}>
                    <Edit2 className="w-3.5 h-3.5 text-stone-500" />
                  </button>
                  <button onClick={() => eliminar(m)} className="p-1.5 rounded hover:bg-red-50" title="Eliminar" data-testid={`btn-eliminar-menu-${m.menu_id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              </div>
              <div className="text-xs text-stone-600 space-y-1">
                {(m.platillos || []).length > 0 && <p>🍽 {m.platillos.length} platillos</p>}
                {m.precio_por_persona > 0 && <p className="font-medium text-emerald-700">${m.precio_por_persona} / persona</p>}
              </div>
              {(m.platillos || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {m.platillos.slice(0, 4).map((p, i) => (
                    <span key={p.receta_id || `p-${i}`} className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-700">{p.nombre}</span>
                  ))}
                  {m.platillos.length > 4 && <span className="text-[10px] text-stone-500">+{m.platillos.length - 4}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {editando && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={cerrar} data-testid="menu-modal">
          <div className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <p className="font-display text-xl">{editando.menu_id ? "Editar menú" : "Nuevo menú"}</p>
              <button onClick={cerrar} className="p-1.5 rounded hover:bg-stone-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="text-xs uppercase text-stone-500">Nombre *</label>
                <input value={editando.nombre || ""} onChange={(e) => setEditando({ ...editando, nombre: e.target.value })} placeholder="Ej. Menú Buffet Familiar" className="eo-input mt-1" data-testid="input-nombre-menu" />
              </div>
              <div>
                <label className="text-xs uppercase text-stone-500">Descripción</label>
                <textarea value={editando.descripcion || ""} onChange={(e) => setEditando({ ...editando, descripcion: e.target.value })} className="eo-input mt-1" rows={2} placeholder="Para quién, ocasión, especialidad…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase text-stone-500">Precio / persona</label>
                  <input type="number" value={editando.precio_por_persona || ""} onChange={(e) => setEditando({ ...editando, precio_por_persona: parseFloat(e.target.value) || 0 })} className="eo-input mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-stone-500">Tipo de servicio</label>
                  <select
                    value={editando.categoria || ""}
                    onChange={(e) => setEditando({ ...editando, categoria: e.target.value })}
                    className="eo-input mt-1"
                    data-testid="select-categoria-menu"
                  >
                    <option value="">— Selecciona —</option>
                    <option value="desayuno">Desayuno</option>
                    <option value="almuerzo">Almuerzo</option>
                    <option value="brunch">Brunch</option>
                    <option value="comida">Comida</option>
                    <option value="cena">Cena</option>
                    <option value="coffee_break">Coffee break</option>
                    <option value="snacks">Carrito de bienvenida / snacks</option>
                    <option value="botanas">Botanas / entradas</option>
                    <option value="postres">Postres / mesa dulce</option>
                    <option value="buffet">Buffet completo</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase text-stone-500">Recetas incluidas ({(editando.platillos || []).length} seleccionadas)</label>
                {recetas.length === 0 ? (
                  <p className="text-xs text-stone-500 mt-2">No tienes recetas. Crea recetas primero en /panel/recetas.</p>
                ) : (
                  <div className="mt-2 max-h-64 overflow-y-auto border border-stone-200 rounded-lg">
                    {recetas.map(r => {
                      const sel = editando.platillos.find(p => p.receta_id === r.receta_id);
                      return (
                        <label key={r.receta_id} className="flex items-center gap-2 px-3 py-2 hover:bg-stone-50 cursor-pointer border-b border-stone-100 last:border-b-0" data-testid={`receta-toggle-${r.receta_id}`}>
                          <input type="checkbox" checked={!!sel} onChange={() => togglePlatillo(r)} className="w-4 h-4" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-stone-900 truncate">{r.nombre}</p>
                            <p className="text-[10px] text-stone-500">{r.tipo || "platillo"} · {r.porciones_base || "—"} porciones · ${r.costo_por_porcion?.toFixed(2) || "—"}/porción</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase text-stone-500">Botanas (texto libre)</label>
                  <input value={editando.botanas || ""} onChange={(e) => setEditando({ ...editando, botanas: e.target.value })} className="eo-input mt-1" placeholder="Ej. Guacamole + totopos" />
                </div>
                <div>
                  <label className="text-xs uppercase text-stone-500">Plato fuerte</label>
                  <input value={editando.plato_fuerte || ""} onChange={(e) => setEditando({ ...editando, plato_fuerte: e.target.value })} className="eo-input mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-stone-500">Postres</label>
                  <input value={editando.postres || ""} onChange={(e) => setEditando({ ...editando, postres: e.target.value })} className="eo-input mt-1" />
                </div>
              </div>

              {/* Iter125 · P1 Fase 4a · Vínculo con Producción */}
              <div className="border-t border-stone-200 pt-3 space-y-3" data-testid="menu-produccion-section">
                <div>
                  <p className="text-sm font-semibold text-stone-700">Producción</p>
                  <p className="text-xs text-stone-500">Define si al contratarse este menú debe generar un item de Producción con la receta vinculada.</p>
                </div>
                <div className="flex items-center justify-between p-2 hover:bg-stone-50 rounded">
                  <label className="flex-1 cursor-pointer text-sm">Requiere producción al contratarse</label>
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={!!editando.requiere_produccion}
                    onChange={(e) => setEditando({ ...editando, requiere_produccion: e.target.checked })}
                    data-testid="menu-requiere-produccion"
                  />
                </div>
                {editando.requiere_produccion && (
                  <div>
                    <label className="text-xs uppercase text-stone-500">Receta vinculada *</label>
                    <select
                      value={editando.receta_id || ""}
                      onChange={(e) => setEditando({ ...editando, receta_id: e.target.value })}
                      className="eo-input mt-1"
                      data-testid="menu-receta-id"
                    >
                      <option value="">— Selecciona una receta —</option>
                      {recetas.map(r => (
                        <option key={r.receta_id} value={r.receta_id}>{r.nombre}</option>
                      ))}
                    </select>
                    {editando.requiere_produccion && !editando.receta_id && (
                      <p className="text-[10px] mt-1 text-amber-700">Sin receta seleccionada, este menú aparecerá como <span className="italic">Requiere configurar receta</span> en Producción.</p>
                    )}
                  </div>
                )}
              </div>

              <ConfiguracionOperativaFields
                value={editando.configuracion_operativa}
                onChange={(cfg) => setEditando({ ...editando, configuracion_operativa: cfg })}
                testidPrefix="config-op-menu"
              />
            </div>
            <div className="border-t px-5 py-4 flex justify-end gap-2 bg-stone-50">
              <button onClick={cerrar} className="eo-btn eo-btn-outline" disabled={saving}>Cancelar</button>
              <button onClick={guardar} className="eo-btn eo-btn-primary" disabled={saving} data-testid="btn-guardar-menu">
                <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenusCRUD;
